/**
 * Hotstuff EIP-712 typed-data constants.
 *
 * Source: https://docs.hotstuff.trade (and `@hotstuff-labs/ts-sdk/src/utils/signing.util.ts`).
 *
 * Every mutating action on Hotstuff is a payload that is:
 *   1. MessagePack-encoded (`@msgpack/msgpack`)
 *   2. `keccak256`-hashed
 *   3. wrapped into an `Action` struct and signed via `wallet.signTypedData(...)`
 *
 * The domain is hardcoded on the backend (`chainId: 1`, Mainnet) regardless of
 * the chain the user's wallet believes it is connected to — identical to the
 * Hyperliquid model. Do NOT block on wallet chain mismatch in the UI; the
 * signature is chain-independent.
 */

/** Sentinel address used in Hotstuff's EIP-712 domain. */
export const HOTSTUFF_VERIFYING_CONTRACT = "0x1234567890123456789012345678901234567890" as const;

export const HOTSTUFF_NETWORK_MAINNET = "Mainnet" as const;
export const HOTSTUFF_NETWORK_TESTNET = "Testnet" as const;

/** EIP-712 domain separator. */
export const HOTSTUFF_APPROVAL_DOMAIN = {
  name: "HotstuffCore",
  version: "1",
  chainId: 1,
  verifyingContract: HOTSTUFF_VERIFYING_CONTRACT,
} as const;

/**
 * EIP-712 types used to sign every Hotstuff `/exchange` payload. Keep the
 * shape in this exact order — the upstream SDK's `signAction` helper relies on
 * the field order for the encoded struct.
 */
export const HOTSTUFF_APPROVAL_TYPES = {
  Action: [
    { name: "source", type: "string" }, // "Mainnet" | "Testnet"
    { name: "hash", type: "bytes32" }, // keccak256(msgpack(actionPayload))
    { name: "txType", type: "uint16" }, // HOTSTUFF_TX_TYPES[actionName]
  ],
} as const;

/**
 * The shape of the EIP-712 `message` parameter the frontend has to sign. Use
 * the matching `HOTSTUFF_TX_TYPES` entry for `txType`.
 */
export interface HotstuffActionMessage extends Record<string, string | number | bigint> {
  source: typeof HOTSTUFF_NETWORK_MAINNET | typeof HOTSTUFF_NETWORK_TESTNET;
  hash: `0x${string}`;
  txType: number;
}

/**
 * Every signed-action op-code exposed by Hotstuff. Values match
 * `EXCHANGE_OP_CODES` in `@hotstuff-labs/ts-sdk`.
 */
export const HOTSTUFF_TX_TYPES = {
  // Account management
  ADD_AGENT: 1201,
  REVOKE_AGENT: 1211,
  UPDATE_PERP_INSTRUMENT_LEVERAGE: 1203,
  APPROVE_BROKER_FEE: 1207,
  CREATE_REFERRAL_CODE: 1208,
  SET_REFERRER: 1209,
  CLAIM_REFERRAL_REWARDS: 1210,

  // Trading
  PLACE_ORDER: 1301,
  CANCEL_BY_OID: 1302,
  CANCEL_ALL: 1311,
  CANCEL_BY_CLOID: 1312,
  CANCEL_BY_INSTRUMENT: 1313,

  // Collateral transfers & withdrawals
  ACCOUNT_SPOT_WITHDRAW_REQUEST: 1002,
  ACCOUNT_DERIVATIVE_WITHDRAW_REQUEST: 1003,
  ACCOUNT_SPOT_BALANCE_TRANSFER_REQUEST: 1051,
  ACCOUNT_DERIVATIVE_BALANCE_TRANSFER_REQUEST: 1052,
  ACCOUNT_INTERNAL_BALANCE_TRANSFER_REQUEST: 1053,

  // Vaults
  DEPOSIT_TO_VAULT: 1401,
  REDEEM_FROM_VAULT: 1402,
} as const;

export type HotstuffTxType = (typeof HOTSTUFF_TX_TYPES)[keyof typeof HOTSTUFF_TX_TYPES];

/**
 * Convert a basis-point integer to Hotstuff's wire format for broker fee
 * rates: a decimal string expressed as a fraction (5 bps → "0.0005").
 *
 * Used to shape the `maxFeeRate` field in `approveBrokerFee` actions and the
 * `brokerConfig.fee` field on every `placeOrder`. 1 bps = 0.0001.
 *
 * We format with 6 decimal places then strip trailing zeros so the wire
 * string matches the canonical shape Hotstuff returns from
 * `/info brokers_check` (e.g. `"0.0005"`, not `"0.000500"`) — byte-for-byte
 * equality matters when we compare our local rate against the server's.
 */
export function bpsToHotstuffFeeRate(bps: number): string {
  if (!Number.isFinite(bps) || bps < 0) {
    throw new Error(`bpsToHotstuffFeeRate: expected a non-negative finite number, got ${bps}`);
  }
  const fixed = (bps / 10_000).toFixed(6);
  return fixed.replace(/\.?0+$/, "") || "0";
}

/**
 * Inverse of `bpsToHotstuffFeeRate` — parses a Hotstuff wire-format decimal
 * string (e.g. `"0.0005"` returned by `/info brokers_check`) into basis
 * points. Truncates below the bps boundary — Hotstuff only exposes rates
 * at whole-bps resolution, so a fractional-bps input indicates a server /
 * client disagreement worth surfacing upstream.
 */
export function hotstuffFeeRateToBps(rate: string): number {
  const parsed = Number.parseFloat(rate);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`hotstuffFeeRateToBps: expected a non-negative decimal string, got ${rate}`);
  }
  return Math.round(parsed * 10_000);
}

/** Maximum length for an agent name (keeps it human-readable in the UI). */
export const HOTSTUFF_AGENT_NAME_MAX_LENGTH = 32;

/**
 * Validity window for an agent session, in ms.
 *
 * Hotstuff's wire contract: `validUntil` is unix **ms** (verified against
 * the upstream TS and Python SDK examples, which both use ms). The server
 * compares the value against `now_ms`; seconds-looking values are rejected
 * as already expired with `"invalid valid until"`.
 *
 * Set to 180 days — the Hotstuff server enforces a 180-day upper bound on
 * `validUntil` and rejects longer windows with `"invalid valid until"`.
 */
export const HOTSTUFF_AGENT_DEFAULT_TTL_MS = 180 * 24 * 60 * 60 * 1000;
